var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import AdminPageLayout from '../../../layout/admin-page-layout';
import DirectorAddEdit from './_components/director-add-edit';
import Wysiwyg from '@rad/components/inputs/wysiwyg';
import ActionButton from '@rad/components/ui/action-button';
import createTable from '@rad/components/ui/table';
import { useFetch, usePromise } from '@rad/hooks';
import { getDirectors, getDirectorTypes, createDirector, updateDirector, deleteDirector } from '../../../common/network/director';
import { getAboutContent, updateAboutContent } from '../../../common/network/district';
import { useToast } from '../../../contexts/toast';
import { useAuth } from '../../../hooks/useAuth';
import { Permission } from '../../../common/routes/types';
const emptyForm = () => ({
    id: null,
    name: '',
    title: '',
    email: '',
    phone: '',
    photoUrl: '',
    photoAttachmentId: null,
    directorType: 'board',
    sortOrder: 0,
});
const AdminContentAbout = () => {
    var _a, _b;
    const toast = useToast();
    const [auth] = useAuth();
    const canCreate = auth.hasPermission([Permission.AboutCreate]);
    const canUpdate = auth.hasPermission([Permission.AboutUpdate]);
    const canDelete = auth.hasPermission([Permission.AboutDelete]);
    const [aboutContent, setAboutContent] = React.useState('');
    const [showModal, setShowModal] = React.useState(false);
    const [selectedType, setSelectedType] = React.useState('board');
    const [directorForm, setDirectorForm] = React.useState(emptyForm());
    const [aboutDirty, setAboutDirty] = React.useState(false);
    const [formErrors, setFormErrors] = React.useState({});
    const [aboutPromise] = useFetch(getAboutContent());
    const aboutData = usePromise(aboutPromise);
    React.useEffect(() => {
        if ((aboutData === null || aboutData === void 0 ? void 0 : aboutData.aboutContent) !== undefined) {
            setAboutContent(aboutData.aboutContent);
        }
    }, [aboutData]);
    const handleAboutChange = (value) => {
        setAboutContent(value);
        setAboutDirty(true);
    };
    const handleSaveAbout = () => __awaiter(void 0, void 0, void 0, function* () {
        yield updateAboutContent(aboutContent);
        setAboutDirty(false);
        toast.success('About content saved');
    });
    const [directorTypesPromise] = useFetch(getDirectorTypes());
    const directorTypes = (_a = usePromise(directorTypesPromise)) !== null && _a !== void 0 ? _a : [];
    const [directorsPromise, refetchDirectors] = useFetch(getDirectors());
    const allDirectors = (_b = usePromise(directorsPromise)) !== null && _b !== void 0 ? _b : [];
    const filteredDirectorsPromise = React.useMemo(() => directorsPromise.then(dirs => dirs.filter(d => d.directorType.name === selectedType)), [directorsPromise, selectedType]);
    const DirectorTable = React.useMemo(() => createTable(filteredDirectorsPromise), [filteredDirectorsPromise]);
    const openAddModal = () => {
        setDirectorForm(Object.assign(Object.assign({}, emptyForm()), { directorType: selectedType }));
        setFormErrors({});
        setShowModal(true);
    };
    const openEditModal = (director) => {
        setDirectorForm(Object.assign(Object.assign({}, director), { directorType: director.directorType.name }));
        setFormErrors({});
        setShowModal(true);
    };
    const handleSave = (form) => __awaiter(void 0, void 0, void 0, function* () {
        setFormErrors({});
        const result = form.id
            ? yield updateDirector(form)
            : yield createDirector(form);
        if (result[0]) {
            setShowModal(false);
            refetchDirectors();
            toast.success(form.id ? 'Member updated' : 'Member added');
        }
        else {
            const errors = {};
            result[1].forEach(err => {
                errors[err.field] = err.message;
            });
            setFormErrors(errors);
            toast.error('Please fix the errors below');
        }
    });
    const handleDelete = (director) => __awaiter(void 0, void 0, void 0, function* () {
        if (confirm(`Are you sure you want to delete ${director.name}?`)) {
            yield deleteDirector(director.id);
            refetchDirectors();
            toast.success('Member deleted');
        }
    });
    return (_jsx(AdminPageLayout, { children: _jsxs("div", { className: "container py-4", children: [_jsx("h5", { className: "fw-semibold mb-3", children: "About" }), _jsxs("div", { className: "mb-4", children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-2", children: [_jsx("div", { className: "fw-semibold", children: "About Our District" }), _jsx(ActionButton, { className: "btn btn-primary btn-sm", onClick: handleSaveAbout, disabled: !aboutDirty, loadingText: "Saving...", children: "Save" })] }), _jsx(Wysiwyg, { value: aboutContent, onChange: handleAboutChange, placeholder: "Write about your district...", minHeight: 280 })] }), _jsxs("div", { className: "mb-4", children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-3", children: [_jsx("div", { className: "fw-semibold", children: "Members" }), canCreate && (_jsx("button", { className: "btn btn-outline-secondary btn-sm", onClick: openAddModal, children: "Add New" }))] }), _jsx("div", { className: "d-flex gap-2 mb-3", children: directorTypes.map(type => (_jsxs("button", { className: `btn btn-sm ${selectedType === type.name ? 'btn-primary' : 'btn-outline-secondary'}`, onClick: () => setSelectedType(type.name), children: [type.display, _jsx("span", { className: "ms-1 badge bg-light text-dark", children: allDirectors.filter(d => d.directorType.name === type.name).length })] }, type.name))) }), _jsxs(DirectorTable, { className: "mb-0", children: [_jsx(DirectorTable.Column, { header: "", className: "text-center", render: director => (director.photoUrl ? (_jsx("img", { src: director.photoUrl, alt: director.name, style: { width: 48, height: 48, objectFit: 'cover' }, className: "rounded" })) : (_jsx("div", { className: "rounded d-flex align-items-center justify-content-center", style: { width: 48, height: 48, backgroundColor: 'var(--panel)' }, children: _jsx("i", { className: "bi bi-person text-muted" }) }))) }), _jsx(DirectorTable.Column, { header: "Name", render: director => (_jsxs(_Fragment, { children: [_jsx("div", { className: "fw-medium", children: director.name }), director.title && _jsx("div", { className: "small text-muted", children: director.title })] })) }), _jsx(DirectorTable.Column, { header: "Email", accessor: "email" }), _jsx(DirectorTable.Column, { header: "Phone", accessor: "phone" }), _jsx(DirectorTable.Column, { header: "", className: "text-end", render: director => (_jsxs(_Fragment, { children: [canUpdate && (_jsx("button", { className: "btn btn-link text-primary p-0 me-2", onClick: () => openEditModal(director), title: "Edit", children: _jsx("i", { className: "bi bi-pencil" }) })), canDelete && (_jsx(ActionButton, { className: "btn btn-link text-danger p-0", onClick: () => handleDelete(director), title: "Delete", children: _jsx("i", { className: "bi bi-trash" }) }))] })) })] })] }), _jsx(DirectorAddEdit, { isOpen: showModal, directorTypes: directorTypesPromise, value: directorForm, onChange: setDirectorForm, onCancel: () => setShowModal(false), onSave: handleSave, errors: formErrors })] }) }));
};
export default AdminContentAbout;
